/**
 * Déduit de la table des sections (`config/sections`) les routes de la
 * console : une route par onglet final, une par fiche de détail, et une
 * redirection pour chaque section ou onglet à sous-onglets. Le menu et les
 * onglets lisant la même table, une adresse visible est toujours routée.
 */
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useRBAC } from '../contexts/RBACContext';
import { UserRole } from '../types/rbac';
import type { Onglet, Section } from '../config/sections';
import SectionPage from '../components/navigation/SectionPage';
import { cheminComplet, premiereFeuilleAutorisee } from '../components/navigation/ongletsVisibles';
import type { AppRoute } from './appRoutes';
import { ANCIENNES_ADRESSES } from './anciennesAdresses';

/** Adresse affichée quand le compte ne voit aucun onglet de la section. */
const ADRESSE_NON_AUTORISE = '/unauthorized';

/** Redirige vers le premier onglet final ouvert au compte sous `onglets`. */
const VersPremierOnglet: React.FC<{ base: string; onglets: Onglet[] }> = ({ base, onglets }) => {
  const { hasRole } = useRBAC();
  const cible = premiereFeuilleAutorisee(base, onglets, hasRole) ?? ADRESSE_NON_AUTORISE;
  return <Navigate to={cible} replace />;
};

/** Les rôles les plus proches de la feuille l'emportent (un enfant restreint). */
const rolesEffectifs = (...niveaux: (UserRole[] | undefined)[]): UserRole[] | undefined =>
  [...niveaux].reverse().find((roles) => roles && roles.length > 0);

/** Contexte de parcours d'un niveau d'onglets. */
interface Niveau {
  section: Section;
  base: string;
  roles: UserRole[] | undefined;
}

/** Routes d'un onglet : feuille et fiches, ou redirection puis sous-onglets. */
const routesDeLOnglet = (onglet: Onglet, niveau: Niveau): AppRoute[] => {
  const chemin = cheminComplet(niveau.base, onglet.chemin);
  const roles = rolesEffectifs(niveau.roles, onglet.roles);
  if (onglet.onglets) {
    const redirection = { path: chemin, element: <VersPremierOnglet base={chemin} onglets={onglet.onglets} /> };
    const enfants = onglet.onglets.flatMap((sous) => routesDeLOnglet(sous, { ...niveau, base: chemin, roles }));
    return [redirection, ...enfants];
  }
  if (!onglet.element) {
    throw new Error(`[routesDesSections] onglet « ${onglet.cle} » (${chemin}) : ni écran ni sous-onglets`);
  }
  const ecrans = [{ chemin, ecran: onglet.element }, ...(onglet.details ?? []).map((detail) => ({
    chemin: cheminComplet(chemin, detail.chemin), ecran: detail.element,
  }))];
  return ecrans.map(({ chemin: path, ecran }) => ({
    path, roles, element: <SectionPage section={niveau.section} ecran={ecran} />,
  }));
};

/** Routes d'une section : redirection de sa racine, puis ses onglets. */
const routesDeLaSection = (section: Section): AppRoute[] => {
  const racine = section.onglets.some((onglet) => cheminComplet(section.chemin, onglet.chemin) === section.chemin)
    ? []
    : [{ path: section.chemin, element: <VersPremierOnglet base={section.chemin} onglets={section.onglets} /> }];
  const niveau = { section, base: section.chemin, roles: section.roles };
  return [...racine, ...section.onglets.flatMap((onglet) => routesDeLOnglet(onglet, niveau))];
};

/** Redirections des anciennes adresses du menu vers leur onglet. */
const routesDesAnciennesAdresses = (): AppRoute[] =>
  Object.entries(ANCIENNES_ADRESSES).map(([ancienne, nouvelle]) => ({
    path: ancienne, element: <Navigate to={nouvelle} replace />,
  }));

/** Toutes les routes déduites des sections, anciennes adresses comprises. */
export const construireRoutes = (sections: Section[]): AppRoute[] => [
  ...sections.flatMap(routesDeLaSection),
  ...routesDesAnciennesAdresses(),
];
